"use client";

import Link from "next/link";
import { m } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Chrome } from "@/components/ui/tls/chrome";
import { categoryBrowseHref, categoryData, categoryNameToSlug } from "@/lib/category-data";
import SoundCard from "@/components/ui/sound-card";
import type { Sound } from "@/lib/supabase";
import { itemVariants } from "@/components/sound-player/shared-motion";

// DS SoundDetail template: "related" is a hairline list of density-2 rows
// (56px) on the column axis, four to six of them; not a carousel, not a
// horizontal scroller. Rows beyond the cap live behind the "All ... sounds"
// link, which is the one navigation affordance this block needs.
const RELATED_ROW_CAP = 6;

export interface RelatedSoundsRailProps {
  sound: Sound;
  relatedSounds: Sound[];
  /** Pre-resolved related-rail heading (TES-810b waterfall). */
  relatedRailTitle?: string;
}

/**
 * Related sounds list (density-2 hairline rows) extracted from
 * sound-player-client.tsx.
 *
 * NOTE: the root element is an `m.div` with the page's stagger item variants,
 * so this component MUST be rendered inside a `<LazyMotion>` ancestor (the
 * sound player page wraps everything in `<LazyMotion features={domAnimation}>`).
 * Renders null when `relatedSounds` is empty.
 */
export function RelatedSoundsRail({
  sound,
  relatedSounds,
  relatedRailTitle,
}: RelatedSoundsRailProps) {
  const catSlug = categoryNameToSlug(sound.category);
  const catInfo = categoryData[catSlug];

  // Subcollection match for related rail label (§5.4).
  const matchedSubcollection = catInfo?.subcollections?.find(sc =>
    sc.keywords.some(kw => {
      const kwLower = kw.toLowerCase();
      return (
        sound.title.toLowerCase().includes(kwLower) ||
        sound.tags?.some(t => t.toLowerCase().includes(kwLower))
      );
    })
  );

  if (relatedSounds.length === 0) return null;

  return (
    <m.div variants={itemVariants}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
        }}
      >
        <div>
          {/* The eyebrow names the section's ROLE; the h2 carries the
              title. Repeating the title in the eyebrow ("YOU MIGHT ALSO
              LIKE" over "You might also like") said the same thing twice at
              two sizes. */}
          <div className="tlsr-section-tick" style={{ marginBottom: 8 }}>
            <Chrome>related</Chrome>
          </div>
          <h2
            style={{
              font: "var(--tls-h3)",
              color: "var(--tls-fg-strong)",
              margin: 0,
              letterSpacing: "-0.02em",
            }}
          >
            {relatedRailTitle
              ? relatedRailTitle
              : matchedSubcollection
                ? `More ${matchedSubcollection.label} sounds`
                : "Related sounds"}
          </h2>
        </div>
        <Link
          href={categoryBrowseHref(sound.category)}
          style={{
            fontSize: 13,
            fontWeight: 600,
            // Same treatment as the homepage section index links ("All ... →"):
            // ink-muted at rest, accent on hover.
            color: "var(--tls-fg-muted)",
            textDecoration: "none",
            display: "inline-flex",
            gap: 6,
            alignItems: "center",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
          className="tlsr-focus hover:text-[color:var(--tls-brand-ink)] transition-colors"
        >
          All {sound.category} sounds <ArrowRight aria-hidden="true" style={{ width: 13, height: 13 }} />
        </Link>
      </div>

      {/* Plain hairline list on the column axis: no chevrons, no indent, the
          hairline spans exactly the content column. */}
      <div
        className="grid grid-cols-1 gap-0"
        style={{ borderTop: "1px solid var(--tls-border-subtle)" }}
      >
        {relatedSounds.slice(0, RELATED_ROW_CAP).map(relatedSound => (
          <SoundCard
            key={relatedSound.id}
            sound={relatedSound}
            showCategory={false}
            density="d2"
            disableEntryAnimation
          />
        ))}
      </div>
    </m.div>
  );
}
